import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import { Button, Popover, ActionList, ButtonGroup, Icon } from 'components/ui';
import InfoTooltip from 'components/shared/InfoTooltip';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { Star, Edit3, Circle } from 'react-feather';
import {
  useGetGlobalSkillsSettings,
  useUpdateGlobalSkillsSettings,
} from './queries/globalSkillsSettings';

const GlobalSkillsSettings = () => {
  const queryClient = useQueryClient();
  const { data } = useGetGlobalSkillsSettings();
  const currentCompanyRatingDefinitions =
    data?.skillsSettings?.ratingDefinitions;
  const currentCompanyRatingIconStyle =
    data?.skillsSettings?.ratingIconStyle ?? 'skills_default';
  const currentCompanyRatingScale = data?.skillsSettings?.ratingScale;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tempIconChoice, setTempIconChoice] = useState('');
  const { mutate } = useUpdateGlobalSkillsSettings({
    ratingIconStyle: '',
    ratingDefinitions: {},
    ratingScale: '',
  });

  useEffect(() => {
    setTempIconChoice(currentCompanyRatingIconStyle);
  }, [currentCompanyRatingIconStyle, data]);

  async function handleSubmit() {
    try {
      await mutate(
        {
          ratingIconStyle: tempIconChoice,
          ratingDefinitions: JSON.stringify(currentCompanyRatingDefinitions),
          ratingScale: currentCompanyRatingScale,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('skillsSettings');
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
    setEditMode(false);
  }

  const renderRatings = number => {
    const ratingDefinitions = Object.values(
      currentCompanyRatingDefinitions ?? [],
    );
    return (
      <>
        <div className="d-f ai-c">
          {editMode ? (
            <>
              <div className="d-f fd-r w-350 ai-c">
                <Text variant={TEXT_VARIANTS.BODY}>{number}</Text>
                <span
                  className={
                    number === 1
                      ? 'llui-text-instrux-error ml-6 mr-2'
                      : 'llui-text-instrux-error ml-4 mr-1'
                  }
                >
                  *
                </span>
                <input
                  placeholder={ratingDefinitions.find(
                    def => ratingDefinitions?.indexOf(def) + 1 === number,
                  )}
                  value={ratingDefinitions.find(
                    def => ratingDefinitions?.indexOf(def) + 1 === number,
                  )}
                  className="fz-16 h-40 ml-10 w-350 p-10"
                />
              </div>
            </>
          ) : (
            <>
              <div className="d-f fd-c w-350">
                <input
                  disabled={!editMode}
                  variant="filled"
                  placeholder={ratingDefinitions.find(
                    def => ratingDefinitions?.indexOf(def) + 1 === number,
                  )}
                  className="fz-16 h-40 pl-10"
                />
              </div>
            </>
          )}
          {tempIconChoice === 'skills_default' ? (
            <div className="d-f mt-16 ai-c fd-r mb-12">
              <div className="mr-8 ml-24">
                <Star className="CustomColor--svgFill" stroke="#949499" />
              </div>
              <div className="mr-8">
                <Star
                  className={number !== 1 && 'CustomColor--svgFill'}
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Star
                  className={
                    number !== 1 && number !== 2 && 'CustomColor--svgFill'
                  }
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Star
                  className={
                    number !== 1 &&
                    number !== 2 &&
                    number !== 3 &&
                    'CustomColor--svgFill'
                  }
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Star
                  className={number === 5 && 'CustomColor--svgFill'}
                  stroke="#949499"
                />
              </div>
            </div>
          ) : (
            <div className="d-f mt-16 ai-c fd-r mb-12">
              <div className="mr-8 ml-24">
                <Circle className="CustomColor--svgFill" stroke="#949499" />
              </div>
              <div className="mr-8">
                <Circle
                  className={number !== 1 && 'CustomColor--svgFill'}
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Circle
                  className={
                    number !== 1 && number !== 2 && 'CustomColor--svgFill'
                  }
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Circle
                  className={
                    number !== 1 &&
                    number !== 2 &&
                    number !== 3 &&
                    'CustomColor--svgFill'
                  }
                  stroke="#949499"
                />
              </div>
              <div className="mr-8">
                <Circle
                  className={number === 5 && 'CustomColor--svgFill'}
                  stroke="#949499"
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderSkillsDefault = () => {
    return (
      <div className="d-f fd-r mt-6">
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star stroke="#949499" />
        </div>
      </div>
    );
  };

  const renderCircles = () => {
    return (
      <div className="d-f fd-r mt-6">
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle stroke="#949499" />
        </div>
      </div>
    );
  };

  const renderRatingPreview = () => {
    return (
      <div className="pt-6">
        {tempIconChoice === 'skills_default'
          ? renderSkillsDefault()
          : renderCircles()}
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="fz-24 fw-b mb-12">
          {t('settings.tab.global_skills.link_text')}
        </div>
        <div className="d-f fd-r jc-sb">
          <div className="fz-20 fw-600">
            {t('settings.tab.global_skills.proficiency_levels')}
          </div>
          {!editMode && (
            <Button
              type="button"
              className="Button Button--linked mb-20 ml-48 mr-n-24"
              onClick={() => setEditMode(true)}
            >
              <div className="d-f ai-c jc-sb">
                <Edit3 className="c-brand-gray-darkest mr-10" size={14} />
                {t('common.edit')}
              </div>
            </Button>
          )}
        </div>
        <div className="fz-16 mb-32">
          {t('settings.tab.global_skills.specify_proficiency_levels')}
        </div>
        <div className="d-f ai-c mb-48 fw-600 c-midnight-70">
          <div>{t('settings.tab.global_skills.choose_icon')}</div>
          <InfoTooltip
            className="ml-4 mr-36 c-midnight-50"
            tooltip={t('settings.tab.global_skills.icon_tooltip')}
          />
          <Popover
            isHighPriority
            rootClassName="d-f"
            visible={popoverOpen}
            onClose={() => setPopoverOpen(!popoverOpen)}
            activator={
              <Button
                type="button"
                disabled={!editMode}
                onClick={() => {
                  setPopoverOpen(!popoverOpen);
                }}
                className="Button Button--linked ml-37"
              >
                <div className="d-f fd-r ai-c">
                  {renderRatingPreview(tempIconChoice)}
                  <Icon
                    className="Select-arrowsIcon pos-i ml-12"
                    name="select_arrows"
                    aria-label="Select Arrow"
                  />
                </div>
              </Button>
            }
          >
            <div className="AppSwitcher-popover bdrs-4 f-o-n">
              <ActionList
                items={[
                  {
                    content: (
                      <div className="d-f fd-r">{renderSkillsDefault()}</div>
                    ),
                    onClick: () => {
                      setTempIconChoice('skills_default');
                      setPopoverOpen(!popoverOpen);
                    },
                  },

                  {
                    content: <div className="d-f fd-r">{renderCircles()}</div>,
                    onClick: () => {
                      setTempIconChoice('circles');
                      setPopoverOpen(!popoverOpen);
                    },
                  },
                ]}
              />
            </div>
          </Popover>
        </div>
        <div className="d-f ai-c mb-12 fw-600 c-midnight-70">
          <div>{t('settings.tab.global_skills.define_levels')}</div>
          <InfoTooltip
            className="ml-4 mr-36 c-midnight-50"
            tooltip={t('settings.tab.global_skills.proficiency_tooltip')}
          />
        </div>
        <div>
          <p
            dangerouslySetInnerHTML={{
              __html: t(
                'settings.tab.global_skills.current_proficiency_scale',
                {
                  skill_level_number: currentCompanyRatingScale,
                },
              ),
            }}
          />
        </div>
        <div className="d-f fd-r ai-c">
          <div className="d-f fd-c">
            {renderRatings(1)}
            {renderRatings(2)}
            {renderRatings(3)}
            {renderRatings(4)}
            {renderRatings(5)}
          </div>
        </div>
        {editMode && (
          <>
            <div className="d-f ai-c mt-16">
              <Icon disabled name="add" className="c-brand-blue mr-2 mt-1" />
              <button type="button" className="IconButton">
                <Text variant={TEXT_VARIANTS.BODY_LINK}>
                  {t('settings.tab.global_skills.add_level')}
                </Text>
              </button>
            </div>
            <div className="d-f fd-r jc-e mt-48">
              <ButtonGroup>
                <Button
                  type="button"
                  className="Button mr-12"
                  onClick={() => setEditMode(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  className="Button Button--customColor w-125"
                  onClick={() => handleSubmit()}
                >
                  {t('common.save')}
                </Button>
              </ButtonGroup>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(GlobalSkillsSettings),
);
